/**
 * DividendInsightCard Component
 *
 * Displays a dividend stock with yield, payout ratio, and price data.
 * Highlights dividend quality metrics for income-focused users.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { InsightConfig } from '../quizTypes';
import type { FMPQuote, FMPRatios } from '../../../services/fmpDataService';
import { typography } from '../../../common/styles/universalTheme';
import InsightCard from './InsightCard';

interface DividendInsightCardProps {
  config: InsightConfig;
  quote: FMPQuote | null;
  ratios: FMPRatios | null;
  isLoading: boolean;
  onNavigate: (route: string) => void;
}

const DividendInsightCard: React.FC<DividendInsightCardProps> = ({
  config,
  quote,
  ratios,
  isLoading,
  onNavigate,
}) => {
  const theme = useTheme();

  // Extract dividend yield (may be in different formats)
  const dividendYield = ratios?.dividendYield ?? ratios?.dividendYieldPercentage ?? null;
  const dividendYieldPercent =
    dividendYield !== null
      ? dividendYield > 1
        ? dividendYield // Already percentage
        : dividendYield * 100 // Convert decimal to percentage
      : null;

  // Extract payout ratio
  const payoutRatio = ratios?.payoutRatio ?? null;
  const payoutRatioPercent =
    payoutRatio !== null
      ? payoutRatio > 1
        ? payoutRatio // Already percentage (>100% means paying more than earnings)
        : payoutRatio * 100 // Convert decimal
      : null;

  // Color coding for payout ratio
  const getPayoutRatioColor = (ratio: number): string => {
    if (ratio < 60) return theme.palette.success.main;
    if (ratio < 80) return theme.palette.text.secondary;
    return theme.palette.error.main;
  };

  // Determine change color
  const changeColor =
    quote && quote.changesPercentage >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  return (
    <InsightCard
      label={config.label}
      rationale={config.rationale}
      deepLinkRoute={config.deepLinkRoute}
      onNavigate={onNavigate}
      isLoading={isLoading}
    >
      {quote && (
        <Box>
          {/* Symbol and company name */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {quote.symbol}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {quote.name}
            </Typography>
          </Box>

          {/* Dividend yield (prominent) */}
          {dividendYieldPercent !== null && (
            <Box sx={{ mb: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontVariantNumeric: 'tabular-nums lining-nums',
                  fontWeight: 700,
                  fontSize: typography.fontSize.lg,
                  color: theme.palette.success.main,
                }}
              >
                {dividendYieldPercent.toFixed(2)}% yield
              </Typography>
            </Box>
          )}

          {/* Price and change */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.75 }}>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontWeight: 500,
                fontSize: typography.fontSize.base,
                color: 'text.primary',
              }}
            >
              ${quote.price.toFixed(2)}
            </Typography>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontSize: typography.fontSize.sm,
                fontWeight: 500,
                color: changeColor,
              }}
            >
              {quote.changesPercentage >= 0 ? '+' : ''}
              {quote.changesPercentage.toFixed(2)}%
            </Typography>
          </Box>

          {/* Payout ratio */}
          {payoutRatioPercent !== null && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                Payout Ratio
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontFamily: typography.fontFamily.mono,
                  fontVariantNumeric: 'tabular-nums',
                  color: getPayoutRatioColor(payoutRatioPercent),
                  fontWeight: 500,
                }}
              >
                {payoutRatioPercent.toFixed(0)}%
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </InsightCard>
  );
};

export default DividendInsightCard;
